import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import type {
    AcademyStats,
    AcademyCreatorItem,
    AcademyVideoItem,
    AcademyRatingFeedbackItem,
    CreatorStatusFilter,
    VideoStatusFilter,
} from '../types';

function parseJsonRpcRows<T>(data: unknown): T[] {
    if (data && typeof data === 'string') {
        try {
            return parseJsonRpcRows<T>(JSON.parse(data) as unknown);
        } catch {
            return [];
        }
    }
    return normalizeRpcResponse(data as T | null | undefined) as T[];
}

// ─── Stats ───────────────────────────────────────────────────────────────────

export function useAcademyStats() {
    return useQuery<AcademyStats>({
        queryKey: ['admin', 'academy', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_academy_stats');
            if (error) throw new Error(error.message);
            const normalized = normalizeRpcResponse(data);
            const row = normalized[0] as Record<string, unknown>;
            if (!row) return {} as AcademyStats;
            return {
                total_creators: Number(row.total_creators ?? 0),
                creators_approved: Number(row.creators_approved ?? 0),
                creators_pending: Number(row.creators_pending ?? 0),
                creators_rejected: Number(row.creators_rejected ?? 0),
                total_videos: Number(row.total_videos ?? 0),
                videos_approved: Number(row.videos_approved ?? 0),
                videos_pending: Number(row.videos_pending ?? 0),
                videos_rejected: Number(row.videos_rejected ?? 0),
                total_ratings: Number(row.total_ratings ?? 0),
                total_comments: Number(row.total_comments ?? 0),
                avg_rating: row.avg_rating != null ? Number(row.avg_rating) : null,
            } as AcademyStats;
        },
        staleTime: 30_000,
    });
}

// ─── Creators ───────────────────────────────────────────────────────────────

export function useAcademyCreators(
    statusFilter: CreatorStatusFilter,
    search: string
) {
    return useQuery<AcademyCreatorItem[]>({
        queryKey: ['admin', 'academy', 'creators', statusFilter, search],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 200 };
            if (statusFilter !== 'all') params.p_status_filter = statusFilter;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_get_creators', params);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as AcademyCreatorItem[];
        },
        staleTime: 30_000,
    });
}

// ─── Videos ──────────────────────────────────────────────────────────────────

export function useAcademyVideos(
    statusFilter: VideoStatusFilter,
    creatorId: string | null,
    search: string
) {
    return useQuery<AcademyVideoItem[]>({
        queryKey: ['admin', 'academy', 'videos', statusFilter, creatorId, search],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 200 };
            if (statusFilter !== 'all') params.p_status_filter = statusFilter;
            if (creatorId) params.p_creator_id = creatorId;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_get_academy_videos', params);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as AcademyVideoItem[];
        },
        staleTime: 30_000,
    });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useUpdateCreatorStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            creatorId,
            newStatus,
            rejectionReason,
        }: {
            creatorId: string;
            newStatus: string;
            rejectionReason?: string;
        }) => {
            const { error } = await dbClient.rpc('admin_update_creator_status', {
                p_creator_id: creatorId,
                p_new_status: newStatus,
                p_rejection_reason: rejectionReason || null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

export function useUpdateCreator() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { error } = await dbClient.rpc('admin_update_creator', params);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

export function useUpdateVideoStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            videoId,
            newStatus,
            rejectionReason,
        }: {
            videoId: string;
            newStatus: string;
            rejectionReason?: string;
        }) => {
            const { error } = await dbClient.rpc('admin_update_academy_video_status', {
                p_video_id: videoId,
                p_new_status: newStatus,
                p_rejection_reason: rejectionReason || null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

/** Rechazar varios vídeos a la vez (selección múltiple) */
export function useBulkRejectVideos() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            videoIds,
            rejectionReason,
        }: {
            videoIds: string[];
            rejectionReason?: string;
        }) => {
            const results = await Promise.all(
                videoIds.map((videoId) =>
                    dbClient.rpc('admin_update_academy_video_status', {
                        p_video_id: videoId,
                        p_new_status: 'rejected',
                        p_rejection_reason: rejectionReason || null,
                    })
                )
            );
            const failed = results.find((r) => r.error);
            if (failed?.error) throw new Error(failed.error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

export function useUpdateVideo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { error } = await dbClient.rpc('admin_update_academy_video', params);
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

export function useDeleteVideo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (videoId: string) => {
            const { error } = await dbClient.rpc('admin_delete_academy_video', {
                p_video_id: videoId,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

export function useInsertVideo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_insert_academy_video', params);
            if (error) throw new Error(error.message);
            return data as string;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

// ─── YouTube Channel Preview & Sync ──────────────────────────────────────────

export interface YouTubeVideoPreview {
    videoId: string;
    title: string;
    description: string;
    thumbUrl: string | null;
    durationSeconds: number | null;
}

export interface YouTubePreviewResult {
    videos: YouTubeVideoPreview[];
    total: number;
}

export interface YouTubeSyncResult {
    inserted: number;
    skipped: number;
    total_fetched: number;
    message: string;
}

export function useYouTubePreview() {
    return useMutation({
        mutationFn: async ({ creatorId, maxVideos }: { creatorId: string; maxVideos?: number }) => {
            const result = await apiClient.post<YouTubePreviewResult>('/youtube-channel-preview', {
                creator_id: creatorId,
                max_videos: maxVideos ?? 200,
            });
            if (result.error) throw new Error(result.error.message);
            if (!result.data) throw new Error('Respuesta vacía');
            return result.data;
        },
    });
}

export function useYouTubeSync() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ creatorId, maxVideos, videoIds }: { creatorId: string; maxVideos?: number; videoIds?: string[] }) => {
            const result = await apiClient.post<YouTubeSyncResult>('/youtube-channel-sync', {
                creator_id: creatorId,
                max_videos: maxVideos ?? 200,
                video_ids: videoIds,
            });
            if (result.error) throw new Error(result.error.message);
            if (!result.data) throw new Error('Respuesta vacía');
            return result.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'academy'] });
        },
    });
}

/** Valoraciones de artículos con comentario de mejora («Ayúdanos a mejorar»). */
export function useAcademyRatingFeedback() {
    return useQuery<AcademyRatingFeedbackItem[]>({
        queryKey: ['admin', 'academy', 'rating-feedback'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_academy_rating_feedback', {
                p_limit: 500,
                p_with_comment_only: true,
                p_articles_only: true,
            });
            if (error) throw new Error(error.message);
            return parseJsonRpcRows<AcademyRatingFeedbackItem>(data);
        },
        staleTime: 30_000,
    });
}
